import { useState, useEffect, useCallback } from 'react';
import { listFolder, ScrapbookFolder, createFolderWithPath } from '@/api';
import FolderTreeView from '../sidebar/FolderTreeView';
import Modal from '../../general/Modal';
import FolderNameDialog from './FolderNameDialog';
import XLight from '@/assets/xLight.svg';
import Tooltip from '../../general/Tooltip';

interface FolderSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (folderId: number) => Promise<void>;
  reportTitle?: string;
}

const FolderSelectionDialog = ({ isOpen, onClose, onSave }: FolderSelectionDialogProps) => {
  const [folders, setFolders] = useState<ScrapbookFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<ScrapbookFolder | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  useEffect(() => {
    // Auto-select first folder when folders are loaded
    if (folders.length > 0 && !selectedFolder) {
      setSelectedFolder(folders[0]);
    }
  }, [folders, selectedFolder]);

  const sortFolderTree = useCallback((folder: ScrapbookFolder) => {
    if (folder.children) {
      folder.children.sort((a, b) => {
        const bothShared = a.isShared && b.isShared;
        const neitherShared = !a.isShared && !b.isShared;
        if (bothShared || neitherShared) {
          return a.name.localeCompare(b.name);
        }
        return Number(a.isShared) - Number(b.isShared);
      });
      folder.children.forEach(sortFolderTree);
    }
  }, []);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFolder(); // Use the same API as main scrapbook

      if (data && data.rootFolder) {
        // Sort the folder tree like in Vue.js version
        sortFolderTree(data.rootFolder);
        setFolders([data.rootFolder]); // Wrap in array like main scrapbook

        // Auto-select the first folder only if no folder is currently selected
        setSelectedFolder((prev) => prev || data.rootFolder);
      } else {
        console.warn('No root folder returned from API:', data);
        setFolders([]);
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [sortFolderTree]);

  useEffect(() => {
    if (isOpen) {
      fetchFolders();
    }
  }, [isOpen, fetchFolders]);

  const handleSave = async () => {
    if (selectedFolder) {
      setLoading(true);
      try {
        await onSave(selectedFolder.id);
        onClose();
      } catch (error) {
        console.error('FolderSelectionDialog save failed:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const handleCreateFolder = async (folderName: string) => {
    if (!folderName.trim() || !selectedFolder) return;

    try {
      const newPath = selectedFolder.path === '/' ? `/${folderName}` : `${selectedFolder.path}/${folderName}`;
      const result = await createFolderWithPath(selectedFolder.id, newPath);

      if (result.succeeded) {
        setShowCreateFolder(false);
        fetchFolders();
      } else {
        console.error('Failed to create folder:', result.message || result);
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      contanerClassName="w-[305px] xs:w-[330px] sm:w-[340px] md:max-w-sm bg-white shadow-xl mx-4 md:mx-0"
    >
      <div className="flex items-center justify-between p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900">Scrapbook Folders</h3>
        <Tooltip placement="bottom" content="Cancel">
          <XLight className="h-6 w-6 cursor-pointer rounded-full hover:bg-gray-100" onClick={onClose} />
        </Tooltip>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-full max-w-xs">
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div className="bg-black-600 h-full animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto rounded-md p-2" data-lenis-prevent>
            {folders.length === 0 ? (
              <div className="flex justify-center py-8 text-gray-500">
                <span>No folders available</span>
              </div>
            ) : (
              <FolderTreeView
                folders={folders}
                selectedFolderId={selectedFolder?.id || 0}
                onFolderSelect={setSelectedFolder}
                draggingEnabled={false}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-b-sm bg-gray-50 p-4 sm:p-6">
        <button
          onClick={() => setShowCreateFolder(true)}
          className="createBtn cursor-pointer rounded-full px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
        >
          Create new folder
        </button>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={loading || !selectedFolder}
            className="cursor-pointer rounded-full bg-black px-2 py-2 text-sm font-medium text-white transition-colors lg:px-4"
          >
            Save to Folder
          </button>
        </div>
      </div>

      <FolderNameDialog
        show={showCreateFolder}
        title="Create new Folder"
        onOk={handleCreateFolder}
        onCancel={() => setShowCreateFolder(false)}
      />
    </Modal>
  );
};

export default FolderSelectionDialog;
